import ListItem from 'components/Luxkit/List/ListItem'
import ListItemPrice from 'components/Luxkit/List/ListItemPrice'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useId } from 'react'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import RadioButton from 'components/Luxkit/Radio/RadioButton'
import { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import noop from 'lib/function/noop'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import Label from 'components/Luxkit/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import Pill from 'components/Luxkit/Pill'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import Plural from 'components/utils/Formatters/Plural'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

export function getOptionLabel(option: OfferDurationOption) {
  if (option.soldOut) {
    return 'Sold out'
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    return 'From'
  }
}

export function getSubtitleLabel(option: OfferDurationOption) {
  if (isLPCTacticalEnabled() && option.hasTactical) {
    return <LabelGroup>
      <Label
        startIcon={<SolidFireIcon colour="urgency" size="XS" />}
        variant="critical"
        kind="plain"
      >
        Limited Time Special
      </Label>
      {(option.discountPercent ?? 0) > 0 && <Pill kind="primary" variant="success" >-{option.discountPercent}%</Pill>}
    </LabelGroup>
  }

  if (option.stayPayTier) {
    return <Label kind="plain" variant="highlight-secondary">
      <Plural singular="night" count={option.stayPayTier.freeNights} withCount /> free
    </Label>
  }

  return undefined
}

interface Props {
  durationOptions: Array<OfferDurationOption>;
  selectedDuration: number;
  onClose?: () => void;
  bundledWithFlightsOnly: boolean;
  currentOfferId?: string;
  bundleOfferId?: string;
  offerId?: string;
  onSelect?: (duration: number) => void
}

function OfferDurationOptions(props: Props) {
  const {
    durationOptions,
    onClose,
    selectedDuration,
    bundledWithFlightsOnly,
    currentOfferId,
    bundleOfferId,
    onSelect = noop,
  } = props
  const dispatch = useOfferPageDispatch()

  const name = useId()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onSelect(duration)
  }, [dispatch, currentOfferId, onClose, onSelect])

  return <>
    {durationOptions.map(option => {
      const price = option.lowestPrice
      const roundedLowestPrice = price ? Math.ceil(price) : price
      return <ListItem
        key={option.duration}
        title={pluralizeToString('night', option.duration)}
        subtitle={getSubtitleLabel(option)}
        data-duration={option.duration}
        onClick={onDurationClick}
        startIcon={<RadioButton name={name} value={option.duration} checked={option.duration === selectedDuration} />}
        disabled={option.soldOut}
      >
        <ListItemPrice
          price={(option.soldOut || bundledWithFlightsOnly || bundleOfferId) ? undefined : roundedLowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(option)}
        />
      </ListItem> },
    )}
  </>
}

export default withOfferPageAndReduxState((state, appState, props) => {
  const currentOfferId = props.offerId ?? state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    durationOptions: isMultiRoom ? getMultiRoomDurationOptions(currentState, appState) : getDurationOptions(state, appState),
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
  }
})(OfferDurationOptions)
